import { MainLayout } from '@root/components/layouts/Index';
import Header from '@root/components/Header';
import Footer from '@root/components/Footer';
import { axiosWithStore, ServerData } from '@root/utils/axios';
import { Page } from '@root/pages/Type';
import withCheckAuth from '@utils/withCheckAuth';
import withLayout from '@root/utils/withLayout';
import { Trans } from 'react-i18next';
import OsSection from '../Components/OsSection/Index';
import ControlPanelSection from '../Components/ControlPanelSection/Index';
import TopSection from './TopSection';
import PricesSection from './PricesSection';

import * as archiveAnimation from './archive.json';
import * as colorPaletteAnimation from './color-palette.json';
import * as documentSettingsAnimation from './document-settings.json';
import withTitle from '@root/utils/withTitle';

export type OrderPlanPrice = {
    price: number;
    priceOld: number;
    savePercentage: number;
};

export type OrderPlanResources = {
    ram: string;
    cpu: string;
    ssd?: string;
    storage?: string;
    bandwidth?: string;
    ip?: string;
    traffic?: string;
};

export type OrderPlan = {
    id: number;
    name: string;
    prices: OrderPlanPrice;
    resources: OrderPlanResources;
};

type ServicePricesProps = {
    noData?: boolean;
    plans: OrderPlan[];
};

export type OrderPlansServerData = ServerData & ServicePricesProps;

const Index: Page<ServicePricesProps> = ({ noData, plans }) => {
    return (
        <>
            <Header />
            <TopSection />
            <PricesSection noData={noData} plans={plans} />
            <OsSection
                title={
                    <Trans i18nKey="os.title" ns="landingServicesDs">
                        OS idiegimas
                    </Trans>
                }
                description={
                    <Trans i18nKey="os.description" ns="landingServicesDs">
                        Aprasymas
                    </Trans>
                }
                logos={['debian', 'ubuntu', 'windows', 'openbsd', 'centos']}
                osNumber="os-ds-100"
            />
            <ControlPanelSection
                title={
                    <Trans i18nKey="controlPanel.title" ns="landingServicesDs">
                        Valdymas
                    </Trans>
                }
                description={
                    <Trans
                        i18nKey="controlPanel.description"
                        ns="landingServicesDs"
                    >
                        Intuityvus ir paprastas serverių valdymas
                    </Trans>
                }
                items={[
                    {
                        id: '0',
                        icon: 'clock',
                        title: (
                            <Trans
                                i18nKey="controlPanel.monitoring.title"
                                ns="landingServicesDs"
                            >
                                Stebejimas
                            </Trans>
                        ),
                        description: (
                            <Trans
                                i18nKey="controlPanel.monitoring.description"
                                ns="landingServicesDs"
                            >
                                Visi serveriai yra stebimi monitoringo
                                sistemomis 24 valandas per parą 7 dienas per
                                savaitę, todėl įvykus gedimui operatyviai jį
                                eliminuosime.
                            </Trans>
                        ),
                        content: archiveAnimation,
                        speed: 1,
                    },
                    {
                        id: '1',
                        icon: 'toggle',
                        title: (
                            <Trans
                                i18nKey="controlPanel.management.title"
                                ns="landingServicesDs"
                            >
                                Kontrole
                            </Trans>
                        ),
                        description: (
                            <Trans
                                i18nKey="controlPanel.management.description"
                                ns="landingServicesDs"
                            >
                                Visi serveriai yra stebimi monitoringo
                                sistemomis 24 valandas per parą 7 dienas per
                                savaitę, todėl įvykus gedimui operatyviai jį
                                eliminuosime.
                            </Trans>
                        ),
                        content: colorPaletteAnimation,
                        speed: 1,
                    },
                    {
                        id: '2',
                        icon: 'male',
                        title: (
                            <Trans
                                i18nKey="controlPanel.administration.title"
                                ns="landingServicesDs"
                            >
                                Administracija
                            </Trans>
                        ),
                        description: (
                            <Trans
                                i18nKey="controlPanel.administration.description"
                                ns="landingServicesDs"
                            >
                                Visi serveriai yra stebimi monitoringo
                                sistemomis 24 valandas per parą 7 dienas per
                                savaitę, todėl įvykus gedimui operatyviai jį
                                eliminuosime.
                            </Trans>
                        ),
                        content: documentSettingsAnimation,
                        speed: 0.45,
                    },
                ]}
            />
            <Footer renderTopButton />
        </>
    );
};

export const getServerSideProps = withCheckAuth(async ({ store }) => {
    const axios = axiosWithStore(store);
    const { plans, noData = false } = await axios
        .get<OrderPlansServerData>('/public/prices/ds/plans')
        .then((response) => response.data)
        .catch(() => ({
            noData: true,
            plans: [],
        }));
    return {
        props: {
            noData,
            plans,
        },
    };
});

export default withTitle(
    {
        translateKey: 'pageTitle',
        keywordsKey: 'pageKeywords',
        descriptionKey: 'pageDescription',
        default: 'Home page',
        namespace: 'landingServicesDs',
        route: 'landing_services_ds'
    },
    withLayout(MainLayout, Index),
);
